import { isEmpty } from "@bodil/core/assert";
import { attribute, attributeGetter, Component, reactive, type Emits } from "@bodil/dom/component";
import { Signal } from "@bodil/signal";
import type { Role } from "aria-attribute-types";
import { html } from "lit";
import { customElement } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";

import { numberShort } from "./lib";

import styles from "./range.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-range": BtkRange;
    }
}

@customElement("btk-range")
export class BtkRange extends Component {
    emits!: Emits<"change">;

    @attribute({ type: Number }) accessor min = 0;
    @attribute({ type: Number }) accessor max = 100;
    @attribute({ type: Number }) accessor step = 1;
    @attribute({ type: Number, reflect: false }) accessor value = 0;
    @attribute({ type: Boolean }) accessor showValue = false;
    @attribute accessor unit = "";

    @attributeGetter get role(): Role {
        return "slider";
    }

    @reactive accessor format: (value: number) => string = numberShort;

    input = createRef<HTMLInputElement>();

    protected override connected() {
        this.useWhileConnected(Signal.effect(() => this.syncRange()));
    }

    protected override updated() {
        this.syncRange();
    }

    private syncRange() {
        const percent = Math.max(0, (this.value - this.min) / (this.max - this.min));
        this.input.value?.style.setProperty("--percent", `${percent * 100}%`);
    }

    private handleInput() {
        this.value = Number.parseFloat(this.input.value!.value);
        this.syncRange();
    }

    private handleChange() {
        this.handleInput();
        this.emit("change");
    }

    render() {
        return html`
            <slot name="label"></slot>
            <div class="input">
                <input
                    ${ref(this.input)}
                    id="range"
                    type="range"
                    min=${this.min}
                    max=${this.max}
                    step=${this.step}
                    .value=${live(this.value.toString())}
                    @input=${this.handleInput}
                    @change=${this.handleChange}
                />
                ${when(
                    this.showValue,
                    () => html`
                        <output for="range"
                            ><span class="value" style="width: ${this.format(this.max).length}ch"
                                >${this.format(this.value)}</span
                            >${when(!isEmpty(this.unit), () => html` ${this.unit}`)}</output
                        >
                    `,
                )}
            </div>
        `;
    }

    static styles = styles;
}
